import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import type {
  CourseRecord,
  LeagueMemberRecord,
  MatchRecord,
  ScoreRecord,
  SeasonPlayerRecord,
  StandaloneRoundRecord,
  SupplementalScorecardRecord,
} from '../api/types.js';
import { useAppState } from './AppState.js';
import { MobileHoleScoreEditor } from './MobileHoleScoreEditor.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { useInvalidateQueries, useLeagueMembers, useRequiredLeagueId, useResolvedActiveSeason, useSyncLeagueFromRoute } from './hooks.js';
import { holeNumbers } from '../lib/courseScorecard.js';
import { formatOneDecimal, formatWholeNumber, randomIdempotencyKey, sumNumbers } from '../lib/formatting.js';
import {
  assignMatchStrokesForView,
  calculateCourseStrokesForView,
  calculateAbsentHoleScoresForView,
  resolveAppliedHandicapForView,
} from '../lib/viewScoring.js';

function memberName(playerId: string, members: LeagueMemberRecord[]): string {
  return members.find((candidate) => candidate.player_id === playerId)?.player?.name || playerId;
}

function scoreKey(matchId: string, playerId: string) {
  return `${matchId}:${playerId}`;
}

function supplementalKey(matchId: string, forPlayerId: string) {
  return `${matchId}:${forPlayerId}`;
}

function emptyGrossDraft(): number[] {
  return Array.from({ length: 9 }, () => Number.NaN);
}

function validHoleScores(values: number[]): boolean {
  return values.every((value) => Number.isInteger(value) && value >= 1 && value <= 20);
}

function createSupplementalDraft(scorecard: SupplementalScorecardRecord | null) {
  return {
    enabled: Boolean(scorecard),
    playerId: scorecard?.player_id ?? '',
    gross: scorecard?.hole_scores?.length ? scorecard.hole_scores.map((value) => Number(value)) : emptyGrossDraft(),
  };
}

export function AdminScoreEntryPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const { season, seasonsQuery, activeSeasonQuery } = useResolvedActiveSeason(leagueId);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMatchDayId = searchParams.get('matchDayId');

  const matchDaysQuery = useQuery({
    queryKey: ['matchDays', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getMatchDays(leagueId, season!.id)).matchDays,
  });
  const coursesQuery = useQuery({
    queryKey: ['courses', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getCourses(leagueId)).courses,
  });
  const membersQuery = useLeagueMembers(leagueId);

  const orderedMatchDays = useMemo(
    () =>
      [...(matchDaysQuery.data ?? [])].sort((left, right) => {
        const dateDiff = left.date.localeCompare(right.date);
        return dateDiff !== 0 ? dateDiff : left.id.localeCompare(right.id);
      }),
    [matchDaysQuery.data],
  );
  const selectedMatchDayId =
    requestedMatchDayId && orderedMatchDays.some((matchDay) => matchDay.id === requestedMatchDayId)
      ? requestedMatchDayId
      : orderedMatchDays.at(-1)?.id ?? null;
  const selectedMatchDay = orderedMatchDays.find((matchDay) => matchDay.id === selectedMatchDayId) ?? null;

  useEffect(() => {
    if (!selectedMatchDayId) {
      return;
    }
    if (requestedMatchDayId === selectedMatchDayId) {
      return;
    }
    setSearchParams({ matchDayId: selectedMatchDayId }, { replace: true });
  }, [requestedMatchDayId, selectedMatchDayId, setSearchParams]);

  const matchesQuery = useQuery({
    queryKey: ['matchDayMatches', leagueId, selectedMatchDayId],
    enabled: Boolean(client && selectedMatchDayId),
    queryFn: async () => (await client!.getMatchDayMatches(leagueId, selectedMatchDayId!)).matches,
  });
  const scoresQuery = useQuery({
    queryKey: ['matchDayScores', leagueId, selectedMatchDayId],
    enabled: Boolean(client && selectedMatchDayId),
    queryFn: async () => client!.getMatchDayScores(leagueId, selectedMatchDayId!),
  });
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, selectedMatchDay?.season_id ?? null],
    enabled: Boolean(client && selectedMatchDay?.season_id),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, selectedMatchDay!.season_id)).seasonPlayers,
  });
  const isPlayoffWeek = Boolean(selectedMatchDay?.is_playoff_week);
  const standaloneRoundsQuery = useQuery({
    queryKey: ['standaloneRounds', leagueId, selectedMatchDayId],
    enabled: Boolean(client && selectedMatchDayId && isPlayoffWeek),
    queryFn: async () => (await client!.getMatchDayStandaloneRounds(leagueId, selectedMatchDayId!)).rounds,
  });

  if (
    seasonsQuery.isLoading ||
    activeSeasonQuery.isLoading ||
    coursesQuery.isLoading ||
    membersQuery.isLoading ||
    (season ? matchDaysQuery.isLoading : false) ||
    (selectedMatchDayId
      ? matchesQuery.isLoading || scoresQuery.isLoading || seasonPlayersQuery.isLoading || (isPlayoffWeek && standaloneRoundsQuery.isLoading)
      : false)
  ) {
    return <LoadingPanel title="Score Entry" message="Loading score entry." />;
  }

  if (
    seasonsQuery.isError ||
    activeSeasonQuery.isError ||
    coursesQuery.isError ||
    membersQuery.isError ||
    matchDaysQuery.isError ||
    matchesQuery.isError ||
    scoresQuery.isError ||
    seasonPlayersQuery.isError ||
    (isPlayoffWeek && standaloneRoundsQuery.isError) ||
    !coursesQuery.data ||
    !membersQuery.data
  ) {
    return (
      <ErrorPanel
        title="Score Entry"
        message={mapError(
          seasonsQuery.error ??
            activeSeasonQuery.error ??
            coursesQuery.error ??
            membersQuery.error ??
            matchDaysQuery.error ??
            matchesQuery.error ??
            scoresQuery.error ??
            seasonPlayersQuery.error ??
            standaloneRoundsQuery.error,
        )}
      />
    );
  }

  const course = coursesQuery.data.find((candidate) => candidate.id === selectedMatchDay?.course_id) ?? null;
  const matches = matchesQuery.data ?? [];
  const scores = scoresQuery.data?.scores ?? [];
  const supplementalCards = scoresQuery.data?.supplementalCards ?? [];
  const members = membersQuery.data;
  const seasonPlayers = seasonPlayersQuery.data ?? [];
  const scoreLookup = new Map(scores.map((score) => [scoreKey(score.match_id, score.player_id), score]));
  const supplementalLookup = new Map(
    supplementalCards.map((scorecard) => [supplementalKey(scorecard.match_id, scorecard.for_player_id), scorecard]),
  );
  const seasonPlayerByPlayerId = new Map(seasonPlayers.map((seasonPlayer) => [seasonPlayer.player_id, seasonPlayer]));
  const activeSeasonPlayerIds = new Set(seasonPlayers.map((seasonPlayer) => seasonPlayer.player_id));
  const eligibleSupplementalMembers = members.filter((member) => !activeSeasonPlayerIds.has(member.player_id));
  const isLocked = selectedMatchDay?.status === 'locked';

  return (
    <>
      <PageIntro
        title="Score Entry"
        actions={
          <Link className="nav-link" to={`/leagues/${leagueId}/admin/match-days`}>
            Match Day Setup
          </Link>
        }
      />
      <section className="panel stack">
        <PanelHeader title="Score Entry" />
        {!season ? (
          <EmptyState title="No active season selected" message="Choose an active season from the header to enter scores." />
        ) : orderedMatchDays.length === 0 ? (
          <EmptyState
            title="No match days yet"
            message="Create a match day before entering scores."
            action={
              <Link className="nav-link" to={`/leagues/${leagueId}/admin/match-days`}>
                Open Match Day Setup
              </Link>
            }
          />
        ) : !selectedMatchDay ? (
          <EmptyState title="No match day selected" message="Choose a match day to enter scores." />
        ) : (
          <>
            <div className="field">
              <label>Match Day</label>
              <select
                value={selectedMatchDayId ?? ''}
                onChange={(event) => setSearchParams({ matchDayId: event.target.value }, { replace: true })}
              >
                {orderedMatchDays.map((matchDay) => (
                  <option key={matchDay.id} value={matchDay.id}>
                    {matchDay.date} - Week {matchDay.weekNumber ?? '?'}
                  </option>
                ))}
              </select>
            </div>
            <p className="muted">
              Status: {selectedMatchDay.status} | Course: {course?.name || 'TBD'} |{' '}
              {selectedMatchDay.hammer_hole ? `Hammer Hole ${selectedMatchDay.hammer_hole}` : 'No hammer hole'}
              {isPlayoffWeek ? ' | Playoff Week' : ''}
            </p>
            {isPlayoffWeek ? (
              <>
                <p className="alert warn">
                  Playoff week: no matchups. Any season player can log a standalone round for skins and handicap
                  tracking. Standings and points are never affected by these rounds.
                </p>
                {!course ? (
                  <p className="alert warn">Course is still marked TBD. Assign a course before entering standalone rounds for this match day.</p>
                ) : (
                  <div className="card-list">
                    {seasonPlayers.map((seasonPlayer) => (
                      <StandaloneRoundCard
                        key={seasonPlayer.player_id}
                        leagueId={leagueId}
                        matchDayId={selectedMatchDay.id}
                        course={course}
                        isLocked={Boolean(isLocked)}
                        playerName={memberName(seasonPlayer.player_id, members)}
                        seasonPlayer={seasonPlayer}
                        eligibleSubMembers={eligibleSupplementalMembers}
                        savedRound={
                          (standaloneRoundsQuery.data ?? []).find((round) => round.for_player_id === seasonPlayer.player_id) ?? null
                        }
                        onSaved={async () => {
                          setBanner('success', 'Standalone round saved.');
                          await invalidate(
                            ['standaloneRounds', leagueId, selectedMatchDay.id],
                            ['matchDay', leagueId, selectedMatchDay.id],
                            ['matchDays', leagueId],
                            ['seasonPlayers', leagueId, selectedMatchDay.season_id],
                            ['profileHistory', leagueId],
                            ['publishedPortalSnapshot', leagueId],
                          );
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : !course ? (
              <p className="alert warn">Course is still marked TBD. Assign a course before entering scores for this match day.</p>
            ) : matches.length === 0 ? (
              <p className="muted">No pairings found.</p>
            ) : (
              <div className="card-list">
                {matches.map((match) => (
                  <MatchScoreEntryCard
                    key={match.id}
                    leagueId={leagueId}
                    matchDayId={selectedMatchDay.id}
                    course={course}
                    match={match}
                    isLocked={Boolean(isLocked)}
                    members={members}
                    scoreA={scoreLookup.get(scoreKey(match.id, match.player_a_id)) ?? null}
                    scoreB={scoreLookup.get(scoreKey(match.id, match.player_b_id)) ?? null}
                    supplementalA={supplementalLookup.get(supplementalKey(match.id, match.player_a_id)) ?? null}
                    supplementalB={supplementalLookup.get(supplementalKey(match.id, match.player_b_id)) ?? null}
                    seasonPlayerA={seasonPlayerByPlayerId.get(match.player_a_id) ?? null}
                    seasonPlayerB={seasonPlayerByPlayerId.get(match.player_b_id) ?? null}
                    eligibleSupplementalMembers={eligibleSupplementalMembers}
                    onSaved={async () => {
                      setBanner('success', 'Score saved.');
                      await invalidate(
                        ['matchDayScores', leagueId, selectedMatchDay.id],
                        ['matchDayMatches', leagueId, selectedMatchDay.id],
                        ['matchDay', leagueId, selectedMatchDay.id],
                        ['matchDays', leagueId],
                        ['match', leagueId, match.id],
                        ['match-scores', leagueId, match.id],
                        ['standings', leagueId],
                        ['standings', leagueId, match.season_id],
                        ['dashboardSnapshot', leagueId, match.season_id],
                        ['seasonMatches', leagueId, match.season_id],
                        ['seasonPlayers', leagueId, match.season_id],
                        ['profileHistory', leagueId],
                      );
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

function MatchScoreEntryCard({
  leagueId,
  matchDayId,
  course,
  match,
  isLocked,
  members,
  scoreA,
  scoreB,
  supplementalA,
  supplementalB,
  seasonPlayerA,
  seasonPlayerB,
  eligibleSupplementalMembers,
  onSaved,
}: {
  leagueId: string;
  matchDayId: string;
  course: CourseRecord;
  match: MatchRecord;
  isLocked: boolean;
  members: LeagueMemberRecord[];
  scoreA: ScoreRecord | null;
  scoreB: ScoreRecord | null;
  supplementalA: SupplementalScorecardRecord | null;
  supplementalB: SupplementalScorecardRecord | null;
  seasonPlayerA: SeasonPlayerRecord | null;
  seasonPlayerB: SeasonPlayerRecord | null;
  eligibleSupplementalMembers: LeagueMemberRecord[];
  onSaved: () => Promise<void>;
}) {
  const { client, setBanner } = useAppState();
  const [absentA, setAbsentA] = useState(Boolean(scoreA?.player_absent));
  const [absentB, setAbsentB] = useState(Boolean(scoreB?.player_absent));
  const [grossA, setGrossA] = useState<number[]>(
    scoreA?.hole_scores?.length ? scoreA.hole_scores.map((value) => Number(value)) : Array.from({ length: 9 }, () => Number.NaN),
  );
  const [grossB, setGrossB] = useState<number[]>(
    scoreB?.hole_scores?.length ? scoreB.hole_scores.map((value) => Number(value)) : Array.from({ length: 9 }, () => Number.NaN),
  );
  const [supplementalDraftA, setSupplementalDraftA] = useState(() => createSupplementalDraft(supplementalA));
  const [supplementalDraftB, setSupplementalDraftB] = useState(() => createSupplementalDraft(supplementalB));

  useEffect(() => {
    setAbsentA(Boolean(scoreA?.player_absent));
    setAbsentB(Boolean(scoreB?.player_absent));
    setGrossA(
      scoreA?.hole_scores?.length
        ? scoreA.hole_scores.map((value) => Number(value))
        : Array.from({ length: 9 }, () => Number.NaN),
    );
    setGrossB(
      scoreB?.hole_scores?.length
        ? scoreB.hole_scores.map((value) => Number(value))
        : Array.from({ length: 9 }, () => Number.NaN),
    );
    setSupplementalDraftA(createSupplementalDraft(supplementalA));
    setSupplementalDraftB(createSupplementalDraft(supplementalB));
  }, [scoreA, scoreB, supplementalA, supplementalB]);

  const handicapIndexA = Number(scoreA?.handicap_index ?? seasonPlayerA?.current_handicap_index ?? seasonPlayerA?.starting_handicap ?? 0);
  const handicapIndexB = Number(scoreB?.handicap_index ?? seasonPlayerB?.current_handicap_index ?? seasonPlayerB?.starting_handicap ?? 0);
  const handicapA = resolveAppliedHandicapForView(scoreA, handicapIndexA, course);
  const handicapB = resolveAppliedHandicapForView(scoreB, handicapIndexB, course);
  const strokesByPlayer = assignMatchStrokesForView(
    match.player_a_id,
    handicapA.courseHandicapValue,
    match.player_b_id,
    handicapB.courseHandicapValue,
    course.hole_handicaps,
  );
  const effectiveGrossA = absentA
    ? scoreA?.player_absent && scoreA.hole_scores?.length
      ? scoreA.hole_scores.map((value) => Number(value))
      : calculateAbsentHoleScoresForView(handicapA.playingHandicapValue, course.hole_pars, course.hole_handicaps)
    : grossA;
  const effectiveGrossB = absentB
    ? scoreB?.player_absent && scoreB.hole_scores?.length
      ? scoreB.hole_scores.map((value) => Number(value))
      : calculateAbsentHoleScoresForView(handicapB.playingHandicapValue, course.hole_pars, course.hole_handicaps)
    : grossB;
  const netA = effectiveGrossA.map((value, index) => (Number.isFinite(value) ? value - (strokesByPlayer[match.player_a_id]?.[index] ?? 0) : Number.NaN));
  const netB = effectiveGrossB.map((value, index) => (Number.isFinite(value) ? value - (strokesByPlayer[match.player_b_id]?.[index] ?? 0) : Number.NaN));
  const supplementalMemberById = new Map(
    eligibleSupplementalMembers
      .concat(
        supplementalA?.player_id && !eligibleSupplementalMembers.find((member) => member.player_id === supplementalA.player_id)
          ? members.filter((member) => member.player_id === supplementalA.player_id)
          : [],
      )
      .concat(
        supplementalB?.player_id && !eligibleSupplementalMembers.find((member) => member.player_id === supplementalB.player_id)
          ? members.filter((member) => member.player_id === supplementalB.player_id)
          : [],
      )
      .map((member) => [member.player_id, member]),
  );
  const activeSupplementalCardA = supplementalA?.player_id === supplementalDraftA.playerId ? supplementalA : null;
  const activeSupplementalCardB = supplementalB?.player_id === supplementalDraftB.playerId ? supplementalB : null;
  const supplementalHandicapIndexA = Number(
    activeSupplementalCardA?.handicap_index ??
      supplementalMemberById.get(supplementalDraftA.playerId)?.provisional_handicap ??
      0,
  );
  const supplementalHandicapIndexB = Number(
    activeSupplementalCardB?.handicap_index ??
      supplementalMemberById.get(supplementalDraftB.playerId)?.provisional_handicap ??
      0,
  );
  const supplementalHandicapA =
    supplementalDraftA.playerId && supplementalDraftA.enabled
      ? resolveAppliedHandicapForView(activeSupplementalCardA, supplementalHandicapIndexA, course)
      : { courseHandicapValue: 0, playingHandicapValue: 0 };
  const supplementalHandicapB =
    supplementalDraftB.playerId && supplementalDraftB.enabled
      ? resolveAppliedHandicapForView(activeSupplementalCardB, supplementalHandicapIndexB, course)
      : { courseHandicapValue: 0, playingHandicapValue: 0 };
  const supplementalStrokesA = calculateCourseStrokesForView(
    supplementalHandicapA.courseHandicapValue,
    course.hole_handicaps,
  );
  const supplementalStrokesB = calculateCourseStrokesForView(
    supplementalHandicapB.courseHandicapValue,
    course.hole_handicaps,
  );
  const supplementalNetA = supplementalDraftA.gross.map((value, index) =>
    Number.isFinite(value) ? value - (supplementalStrokesA[index] ?? 0) : Number.NaN,
  );
  const supplementalNetB = supplementalDraftB.gross.map((value, index) =>
    Number.isFinite(value) ? value - (supplementalStrokesB[index] ?? 0) : Number.NaN,
  );
  const holes = holeNumbers();
  const playerAName = memberName(match.player_a_id, members);
  const playerBName = memberName(match.player_b_id, members);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      const validGrossA = absentA || effectiveGrossA.every((value) => Number.isInteger(value) && value >= 1 && value <= 20);
      const validGrossB = absentB || effectiveGrossB.every((value) => Number.isInteger(value) && value >= 1 && value <= 20);
      if (!validGrossA || !validGrossB) {
        throw new Error('Each non-absent player must have 9 integer hole scores (1-20) before saving the match.');
      }

      const supplementalCards: Array<{
        matchId: string;
        forPlayerId: string;
        playerId: string;
        participantType: 'sub';
        holeScores: number[];
      }> = [];
      if (absentA && supplementalDraftA.enabled) {
        if (!supplementalDraftA.playerId) {
          throw new Error(`Choose a sub player for ${playerAName}.`);
        }
        if (!validHoleScores(supplementalDraftA.gross)) {
          throw new Error('Each supplemental scorecard must have 9 integer hole scores (1-20) before saving.');
        }
        supplementalCards.push({
          matchId: match.id,
          forPlayerId: match.player_a_id,
          playerId: supplementalDraftA.playerId,
          participantType: 'sub',
          holeScores: supplementalDraftA.gross,
        });
      }
      if (absentB && supplementalDraftB.enabled) {
        if (!supplementalDraftB.playerId) {
          throw new Error(`Choose a sub player for ${playerBName}.`);
        }
        if (!validHoleScores(supplementalDraftB.gross)) {
          throw new Error('Each supplemental scorecard must have 9 integer hole scores (1-20) before saving.');
        }
        supplementalCards.push({
          matchId: match.id,
          forPlayerId: match.player_b_id,
          playerId: supplementalDraftB.playerId,
          participantType: 'sub',
          holeScores: supplementalDraftB.gross,
        });
      }

      return client.submitMatchDayScores(
        leagueId,
        {
          matchDayId,
          scores: [
            {
              matchId: match.id,
              playerId: match.player_a_id,
              playerAbsent: absentA,
              ...(absentA ? {} : { holeScores: effectiveGrossA }),
            },
            {
              matchId: match.id,
              playerId: match.player_b_id,
              playerAbsent: absentB,
              ...(absentB ? {} : { holeScores: effectiveGrossB }),
            },
          ],
          supplementalCards,
          replaceSupplementalCards: true,
        },
        randomIdempotencyKey(),
      );
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  return (
    <div className="card">
      <h4 style={{ marginTop: 0 }}>
        {playerAName} vs {playerBName}
      </h4>
      <p className="muted">Date: {match.match_date}</p>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!isLocked) {
            void saveMutation.mutateAsync();
          }
        }}
      >
        <div className="row">
          <div className="field">
            <label className="absent-toggle">
              <input
                type="checkbox"
                checked={absentA}
                disabled={isLocked}
                onChange={(event) => setAbsentA(event.target.checked)}
              />
              {playerAName} Absent
            </label>
          </div>
          <div className="field">
            <label className="absent-toggle">
              <input
                type="checkbox"
                checked={absentB}
                disabled={isLocked}
                onChange={(event) => setAbsentB(event.target.checked)}
              />
              {playerBName} Absent
            </label>
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label>{playerAName} Handicap Applied</label>
            <div className="scorecard-total">
              Index: {formatOneDecimal(handicapIndexA)} | Course: {formatWholeNumber(handicapA.courseHandicapValue)}
            </div>
          </div>
          <div className="field">
            <label>{playerBName} Handicap Applied</label>
            <div className="scorecard-total">
              Index: {formatOneDecimal(handicapIndexB)} | Course: {formatWholeNumber(handicapB.courseHandicapValue)}
            </div>
          </div>
        </div>
        <MobileHoleScoreEditor
          rows={[
            {
              playerName: playerAName,
              gross: effectiveGrossA,
              editableGross: grossA,
              absent: absentA,
              disabled: isLocked,
              strokes: strokesByPlayer[match.player_a_id] ?? Array.from({ length: 9 }, () => 0),
              net: netA,
              onChange: (holeIndex, value) =>
                setGrossA((current) => current.map((entry, index) => (index === holeIndex ? value : entry))),
            },
            {
              playerName: playerBName,
              gross: effectiveGrossB,
              editableGross: grossB,
              absent: absentB,
              disabled: isLocked,
              strokes: strokesByPlayer[match.player_b_id] ?? Array.from({ length: 9 }, () => 0),
              net: netB,
              onChange: (holeIndex, value) =>
                setGrossB((current) => current.map((entry, index) => (index === holeIndex ? value : entry))),
            },
          ]}
        />
        <div className="scorecard-wrap desktop-score-editor">
          <table className="scorecard-grid match-scorecard">
            <tbody>
              <tr>
                <th>Row</th>
                {holes.map((hole) => (
                  <td key={`hole-${hole}`}>{hole}</td>
                ))}
                <td>Total</td>
              </tr>
              <ScoreRows
                playerName={playerAName}
                gross={effectiveGrossA}
                editableGross={grossA}
                absent={absentA}
                disabled={isLocked}
                strokes={strokesByPlayer[match.player_a_id] ?? Array.from({ length: 9 }, () => 0)}
                net={netA}
                onChange={(holeIndex, value) =>
                  setGrossA((current) => current.map((entry, index) => (index === holeIndex ? value : entry)))
                }
              />
              <tr className="scorecard-separator" aria-hidden="true">
                <td colSpan={holes.length + 2}></td>
              </tr>
              <ScoreRows
                playerName={playerBName}
                gross={effectiveGrossB}
                editableGross={grossB}
                absent={absentB}
                disabled={isLocked}
                strokes={strokesByPlayer[match.player_b_id] ?? Array.from({ length: 9 }, () => 0)}
                net={netB}
                onChange={(holeIndex, value) =>
                  setGrossB((current) => current.map((entry, index) => (index === holeIndex ? value : entry)))
                }
              />
            </tbody>
          </table>
        </div>
        {absentA ? (
          <SupplementalScorecardSection
            scheduledPlayerName={playerAName}
            draft={supplementalDraftA}
            savedCard={supplementalA}
            disabled={isLocked || saveMutation.isPending}
            eligibleMembers={Array.from(supplementalMemberById.values())}
            handicapIndex={supplementalHandicapIndexA}
            courseHandicap={supplementalHandicapA.courseHandicapValue}
            strokes={supplementalStrokesA}
            net={supplementalNetA}
            onDraftChange={setSupplementalDraftA}
          />
        ) : null}
        {absentB ? (
          <SupplementalScorecardSection
            scheduledPlayerName={playerBName}
            draft={supplementalDraftB}
            savedCard={supplementalB}
            disabled={isLocked || saveMutation.isPending}
            eligibleMembers={Array.from(supplementalMemberById.values())}
            handicapIndex={supplementalHandicapIndexB}
            courseHandicap={supplementalHandicapB.courseHandicapValue}
            strokes={supplementalStrokesB}
            net={supplementalNetB}
            onDraftChange={setSupplementalDraftB}
          />
        ) : null}
        <p className="muted">Strokes are calculated from course handicap difference. Net rows update while you type gross scores.</p>
        <div className="inline-actions">
          <button type="submit" disabled={isLocked || saveMutation.isPending}>
            Save Match Scores
          </button>
        </div>
      </form>
    </div>
  );
}

function ScoreRows({
  playerName,
  gross,
  editableGross,
  absent,
  disabled,
  strokes,
  net,
  onChange,
}: {
  playerName: string;
  gross: number[];
  editableGross: number[];
  absent: boolean;
  disabled: boolean;
  strokes: number[];
  net: number[];
  onChange: (holeIndex: number, value: number) => void;
}) {
  const holes = holeNumbers();

  return (
    <>
      <tr>
        <th>{playerName} Gross</th>
        {holes.map((hole, index) => (
          <td key={`${playerName}-gross-${hole}`}>
            <input
              className="scorecard-input score-entry-input"
              type="number"
              min={1}
              max={20}
              step={1}
              disabled={disabled || absent}
              value={absent ? gross[index] ?? '' : Number.isFinite(editableGross[index]!) ? editableGross[index]! : ''}
              onChange={(event) =>
                onChange(index, event.target.value === '' ? Number.NaN : Number(event.target.value))
              }
            />
          </td>
        ))}
        <td>{gross.every((value) => Number.isFinite(value)) ? sumNumbers(gross) : '-'}</td>
      </tr>
      <tr>
        <th>{playerName} Strokes</th>
        {holes.map((hole, index) => (
          <td key={`${playerName}-strokes-${hole}`} className="scorecard-static-cell">
            {strokes[index] ?? 0}
          </td>
        ))}
        <td className="scorecard-static-cell">{sumNumbers(strokes)}</td>
      </tr>
      <tr>
        <th>{playerName} Net</th>
        {holes.map((hole, index) => (
          <td key={`${playerName}-net-${hole}`} className="scorecard-static-cell">
            {Number.isFinite(net[index]!) ? net[index] : '-'}
          </td>
        ))}
        <td className="scorecard-static-cell">{net.every((value) => Number.isFinite(value)) ? sumNumbers(net) : '-'}</td>
      </tr>
    </>
  );
}

export function SupplementalScorecardSection({
  scheduledPlayerName,
  draft,
  savedCard,
  disabled,
  eligibleMembers,
  handicapIndex,
  courseHandicap,
  strokes,
  net,
  onDraftChange,
}: {
scheduledPlayerName: string;
draft: {
  enabled: boolean;
  playerId: string;
  gross: number[];
};
  savedCard: SupplementalScorecardRecord | null;
  disabled: boolean;
  eligibleMembers: LeagueMemberRecord[];
  handicapIndex: number;
  courseHandicap: number;
  strokes: number[];
  net: number[];
  onDraftChange: (value: {
    enabled: boolean;
    playerId: string;
    gross: number[];
  }) => void;
}) {
  const selectedMember = eligibleMembers.find((member) => member.player_id === draft.playerId) ?? null;
  const playerLabel = selectedMember?.player?.name || savedCard?.player_id || 'Sub';
  const holes = holeNumbers();
  const playerSelectId = `${scheduledPlayerName.replace(/\s+/g, '-').toLowerCase()}-supplemental-player`;

  return (
    <section className="panel stack">
      <PanelHeader
        title={`${scheduledPlayerName} Sub Card`}
        subtitle="Optional non-league sub card for handicap tracking and future skins. League points and absent scoring stay unchanged."
      />
      <div className="inline-actions">
        <button
          type="button"
          className="secondary"
          disabled={disabled}
          onClick={() =>
            onDraftChange({
              ...draft,
              enabled: !draft.enabled,
            })
          }
        >
          {draft.enabled ? 'Remove Sub Card' : 'Add Sub Card'}
        </button>
      </div>
      {draft.enabled ? (
        <>
          {eligibleMembers.length === 0 ? (
            <p className="muted">Add the sub as a league member before saving a supplemental scorecard.</p>
          ) : null}
          <div className="row">
            <div className="field">
              <label htmlFor={playerSelectId}>Player</label>
              <select
                id={playerSelectId}
                value={draft.playerId}
                disabled={disabled}
                onChange={(event) =>
                  onDraftChange({
                    ...draft,
                    playerId: event.target.value,
                  })
                }
              >
                <option value="">Select player...</option>
                {eligibleMembers.map((member) => (
                  <option key={member.player_id} value={member.player_id}>
                    {member.player?.name || member.player_id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="muted">
            Applied handicap uses the saved card when present. Before first save, draft preview falls back to the league member provisional handicap until the backend resolves the latest league history.
          </div>
          <div className="scorecard-total">
            Index: {formatOneDecimal(handicapIndex)} | Course: {formatWholeNumber(courseHandicap)}
          </div>
          <MobileHoleScoreEditor
            rows={[{
              playerName: playerLabel,
              gross: draft.gross,
              editableGross: draft.gross,
              absent: false,
              disabled,
              strokes,
              net,
              onChange: (holeIndex, value) =>
                onDraftChange({
                  ...draft,
                  gross: draft.gross.map((entry, index) => (index === holeIndex ? value : entry)),
                }),
            }]}
          />
          <div className="scorecard-wrap desktop-score-editor">
            <table className="scorecard-grid match-scorecard">
              <tbody>
                <tr>
                  <th>Row</th>
                  {holes.map((hole) => (
                    <td key={`${scheduledPlayerName}-supp-hole-${hole}`}>{hole}</td>
                  ))}
                  <td>Total</td>
                </tr>
                <ScoreRows
                  playerName={playerLabel}
                  gross={draft.gross}
                  editableGross={draft.gross}
                  absent={false}
                  disabled={disabled}
                  strokes={strokes}
                  net={net}
                  onChange={(holeIndex, value) =>
                    onDraftChange({
                      ...draft,
                      gross: draft.gross.map((entry, index) => (index === holeIndex ? value : entry)),
                    })
                  }
                />
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}

function StandaloneRoundCard({
  leagueId,
  matchDayId,
  course,
  isLocked,
  playerName,
  seasonPlayer,
  eligibleSubMembers,
  savedRound,
  onSaved,
}: {
  leagueId: string;
  matchDayId: string;
  course: CourseRecord;
  isLocked: boolean;
  playerName: string;
  seasonPlayer: SeasonPlayerRecord;
  eligibleSubMembers: LeagueMemberRecord[];
  savedRound: StandaloneRoundRecord | null;
  onSaved: () => Promise<void>;
}) {
  const { client, setBanner } = useAppState();
  const [absent, setAbsent] = useState(Boolean(savedRound?.player_absent));
  const [gross, setGross] = useState<number[]>(
    savedRound && savedRound.participant_type !== 'sub' && savedRound.hole_scores?.length
      ? savedRound.hole_scores.map((value) => Number(value))
      : emptyGrossDraft(),
  );
  const savedSubCard = savedRound?.participant_type === 'sub' ? savedRound : null;
  const savedSubCardAsSupplemental = savedSubCard as unknown as SupplementalScorecardRecord | null;
  const [subDraft, setSubDraft] = useState(() => createSupplementalDraft(savedSubCardAsSupplemental));

  useEffect(() => {
    setAbsent(Boolean(savedRound?.player_absent));
    setGross(
      savedRound && savedRound.participant_type !== 'sub' && savedRound.hole_scores?.length
        ? savedRound.hole_scores.map((value) => Number(value))
        : emptyGrossDraft(),
    );
    setSubDraft(createSupplementalDraft((savedRound?.participant_type === 'sub' ? savedRound : null) as unknown as SupplementalScorecardRecord | null));
  }, [savedRound]);

  const standaloneCard = savedRound?.participant_type !== 'sub' ? savedRound : null;
  const handicapIndex = Number(standaloneCard?.handicap_index ?? seasonPlayer.current_handicap_index ?? seasonPlayer.starting_handicap ?? 0);
  const appliedHandicap = resolveAppliedHandicapForView(standaloneCard, handicapIndex, course);
  const strokes = calculateCourseStrokesForView(appliedHandicap.courseHandicapValue, course.hole_handicaps);
  const net = gross.map((value, index) => (Number.isFinite(value) ? value - (strokes[index] ?? 0) : Number.NaN));
  const holes = holeNumbers();

  const subMemberById = new Map(
    eligibleSubMembers
      .concat(
        savedSubCard?.player_id && !eligibleSubMembers.find((member) => member.player_id === savedSubCard.player_id)
          ? eligibleSubMembers.filter((member) => member.player_id === savedSubCard.player_id)
          : [],
      )
      .map((member) => [member.player_id, member]),
  );
  const subHandicapIndex = Number(
    savedSubCard?.handicap_index ?? subMemberById.get(subDraft.playerId)?.provisional_handicap ?? 0,
  );
  const subHandicap =
    subDraft.playerId && subDraft.enabled
      ? resolveAppliedHandicapForView(savedSubCard, subHandicapIndex, course)
      : { courseHandicapValue: 0, playingHandicapValue: 0 };
  const subStrokes = calculateCourseStrokesForView(subHandicap.courseHandicapValue, course.hole_handicaps);
  const subNet = subDraft.gross.map((value, index) => (Number.isFinite(value) ? value - (subStrokes[index] ?? 0) : Number.NaN));

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      if (absent) {
        if (subDraft.enabled) {
          if (!subDraft.playerId) {
            throw new Error(`Choose a sub player for ${playerName}.`);
          }
          if (!validHoleScores(subDraft.gross)) {
            throw new Error('Each sub scorecard must have 9 integer hole scores (1-20) before saving.');
          }
          return client.submitStandaloneRound(leagueId, matchDayId, {
            playerId: seasonPlayer.player_id,
            playerAbsent: true,
            subPlayerId: subDraft.playerId,
            subHoleScores: subDraft.gross,
          });
        }
        return client.submitStandaloneRound(leagueId, matchDayId, {
          playerId: seasonPlayer.player_id,
          playerAbsent: true,
        });
      }
      if (!validHoleScores(gross)) {
        throw new Error(`Enter 9 integer hole scores (1-20) for ${playerName} before saving.`);
      }
      return client.submitStandaloneRound(leagueId, matchDayId, {
        playerId: seasonPlayer.player_id,
        playerAbsent: false,
        holeScores: gross,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!client || !savedRound) {
        throw new Error('No standalone round to delete');
      }
      return client.deleteStandaloneRound(leagueId, savedRound.id);
    },
    onSuccess: async () => {
      setGross(emptyGrossDraft());
      setAbsent(false);
      setSubDraft(createSupplementalDraft(null));
      await onSaved();
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  return (
    <div className="card">
      <h4 style={{ marginTop: 0 }}>{playerName}</h4>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!isLocked) {
            void saveMutation.mutateAsync();
          }
        }}
      >
        <div className="field">
          <label className="absent-toggle">
            <input
              type="checkbox"
              checked={absent}
              disabled={isLocked}
              onChange={(event) => setAbsent(event.target.checked)}
            />
            {playerName} Absent
          </label>
        </div>
        {!absent ? (
          <>
            <div className="scorecard-total">
              Index: {formatOneDecimal(handicapIndex)} | Course: {formatWholeNumber(appliedHandicap.courseHandicapValue)}
            </div>
            <MobileHoleScoreEditor
              rows={[{
                playerName,
                gross,
                editableGross: gross,
                absent: false,
                disabled: isLocked,
                strokes,
                net,
                onChange: (holeIndex, value) =>
                  setGross((current) => current.map((entry, index) => (index === holeIndex ? value : entry))),
              }]}
            />
            <div className="scorecard-wrap desktop-score-editor">
              <table className="scorecard-grid match-scorecard">
                <tbody>
                  <tr>
                    <th>Row</th>
                    {holes.map((hole) => (
                      <td key={`${playerName}-standalone-hole-${hole}`}>{hole}</td>
                    ))}
                    <td>Total</td>
                  </tr>
                  <ScoreRows
                    playerName={playerName}
                    gross={gross}
                    editableGross={gross}
                    absent={false}
                    disabled={isLocked}
                    strokes={strokes}
                    net={net}
                    onChange={(holeIndex, value) => setGross((current) => current.map((entry, index) => (index === holeIndex ? value : entry)))}
                  />
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <SupplementalScorecardSection
            scheduledPlayerName={playerName}
            draft={subDraft}
            savedCard={savedSubCardAsSupplemental}
            disabled={isLocked || saveMutation.isPending}
            eligibleMembers={Array.from(subMemberById.values())}
            handicapIndex={subHandicapIndex}
            courseHandicap={subHandicap.courseHandicapValue}
            strokes={subStrokes}
            net={subNet}
            onDraftChange={setSubDraft}
          />
        )}
        <div className="inline-actions">
          <button type="submit" disabled={isLocked || saveMutation.isPending}>
            {savedRound ? 'Update Round' : 'Save Round'}
          </button>
          {savedRound ? (
            <button className="danger" type="button" disabled={isLocked || deleteMutation.isPending} onClick={() => void deleteMutation.mutateAsync()}>
              Delete Round
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
